import classNames from 'classnames';
import {
  AriaAttributes,
  KeyboardEvent,
  PropsWithChildren,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Grey, White } from '../../styles/Colors';
import { ReactComponent as CloseIcon } from '../assets/icons/CloseIcon.svg';
import IconButton from '../Button/IconButton';
import { StyledModal } from './StyledModal';

// What the browser puts in the sequential tab order. Deliberately narrow: the
// dialog holds buttons, one text field (the recording-name dialog of
// Record.tsx, ISSUE-056) and the odd link today, and a wider selector buys
// nothing until it does.
//
// ROUTES THIS SELECTOR CANNOT SEE (it matches the DOM, not the tab order):
//  1. A match that is not actually tabbable — `display:none`, `visibility:
//     hidden`, the `hidden` attribute, or an `inert` ancestor. Such a node
//     becomes a PHANTOM last stop, `document.activeElement !== edge` is then
//     always true and the trap stops firing SILENTLY. Nothing renders one in a
//     dialog today: ConfirmModal drops the cancel button by conditional
//     render (ConfirmModal.tsx:64) rather than hiding it, SaveModal's three
//     buttons and Record's name field are unconditional, and ModelSelect's
//     overlay holds only Loading's two decorative <span>s.
//  2. A positive `tabindex`, which reorders the real tab sequence away from
//     DOM order. None exists in src/ today.
//  3. Element types left out on purpose: contenteditable, iframe, audio/video
//     [controls], area[href], summary.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface ModalProps
  extends Pick<
    AriaAttributes,
    'aria-label' | 'aria-labelledby' | 'aria-describedby'
  > {
  isOpen: boolean;
  onClose?: () => void;
  isPortal?: boolean;
  hideCloseButton?: boolean;
  hideBackdrop?: boolean;
  backdropColor?: string;
  className?: string;
  /**
   * `alertdialog` for acknowledgements — a message the user can only take note
   * of (ISSUE-009's duplicate-file warning), which assistive technology
   * interrupts for. Anything offering a real choice stays a `dialog`.
   */
  role?: 'dialog' | 'alertdialog';
}

/**
 * ISSUE-055. The caller owns the accessible name: pass `aria-labelledby`
 * pointing at the `ModalHeader` (or, when there is no header, at the
 * `ModalBody` — an acknowledgement is named by its message) or `aria-label`
 * for a modal with no text of its own. A modal with neither a name source nor
 * an `aria-label` reaches assistive technology anonymous.
 */
const Modal = ({
  children,
  isOpen,
  onClose,
  isPortal = true,
  hideCloseButton = false,
  hideBackdrop = false,
  backdropColor = Grey['800'],
  className,
  role = 'dialog',
  ...aria
}: PropsWithChildren<ModalProps>) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const portal = document.getElementById('portal');
  const isVisible = !!portal && isOpen;

  useEffect(() => {
    if (!isVisible) return;
    // The control that opened the dialog, captured before focus moves.
    const trigger = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      // Restore on close AND on unmount. No "is it still in the document?"
      // guard: focusing a node that has left the DOM (a trigger whose panel
      // collapsed — NOT a list row; no list row raises a dialog here) is a
      // silent no-op in jsdom and the browser, and focus falls back to body.
      trigger?.focus();
    };
  }, [isVisible]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      // Deliberately the SAME callback the backdrop click uses — a modal has
      // one dismissal path, not a keyboard-only second one. A modal with no
      // onClose (the busy overlay of ModelSelect) stays put, as it should.
      //
      // ROUTE THIS CANNOT SEE: a descendant that handles Escape itself. There
      // is no `event.defaultPrevented` check here, so such a descendant would
      // close the dialog underneath its own dismissal — Select.tsx:143 closes
      // its listbox on Escape with preventDefault() and no stopPropagation().
      // No Select is inside a Modal today (ModelSelect.tsx:57 renders the
      // overlay as a SIBLING of its <Select>, and portals it to #portal
      // besides), but ConfirmModalState.message/title are ReactNode
      // (stores/cvc.ts:553-554), so a caller can put one there.
      onClose?.();
      return;
    }
    if (event.key !== 'Tab') return;

    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = Array.from(
      dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    );
    const edge = event.shiftKey
      ? focusable[0]
      : focusable[focusable.length - 1];
    // Focus rests on the dialog itself right after it opens, and permanently
    // when it holds nothing focusable — Tab from there would land behind the
    // overlay, so that case is an edge too.
    if (document.activeElement !== edge && document.activeElement !== dialog) {
      return;
    }
    event.preventDefault();
    const wrapTo = event.shiftKey
      ? focusable[focusable.length - 1]
      : focusable[0];
    (wrapTo ?? dialog).focus();
  };

  // Unchanged: a missing #portal element means no modal at all, on BOTH the
  // portal and the inline path (several tests depend on it).
  if (!portal || !isOpen) return null;

  // NOT a nested component: declaring one here would give it a new identity on
  // every render, remounting the subtree and re-firing the focus effect.
  const content = (
    <StyledModal
      className={classNames('sup-modal', className)}
      backdropColor={backdropColor}
      isPortal={isPortal}
    >
      {!hideBackdrop && <div className="modal-overlay" onClick={onClose}></div>}
      <div
        className="modal"
        role={role}
        aria-modal="true"
        tabIndex={-1}
        ref={dialogRef}
        onKeyDown={handleKeyDown}
        {...aria}
      >
        {!hideCloseButton && (
          <IconButton
            variant="none"
            aria-label={t('Close') as string}
            onClick={onClose}
            color={White}
            className="modal-close-button"
          >
            <CloseIcon />
          </IconButton>
        )}
        {children}
      </div>
    </StyledModal>
  );

  return isPortal ? createPortal(content, portal) : content;
};

export default Modal;
